import type { CSSProperties, RefObject } from "react";

type AnchorRect = Pick<DOMRect, "left" | "top" | "bottom">;
type BoundaryRect = Pick<DOMRect, "left" | "right" | "top" | "bottom">;

export interface AnnotationFloatingOptions {
    width: number;
    height: number;
    placement?: "above" | "below";
}

export interface AnnotationFloatingEnvironment {
    viewportWidth: number;
    viewportHeight: number;
    boundary?: BoundaryRect | null;
}

export function getAnnotationFloatingStyle(
    rootRef: RefObject<HTMLDivElement | null>,
    rect: AnchorRect,
    options: AnnotationFloatingOptions,
): CSSProperties {
    const viewportWidth = typeof window === "undefined" ? 1280 : window.innerWidth;
    const viewportHeight = typeof window === "undefined" ? 800 : window.innerHeight;
    return computeAnnotationFloatingStyle(rect, options, {
        viewportWidth,
        viewportHeight,
        boundary: rootRef.current?.getBoundingClientRect() ?? null,
    });
}

export function computeAnnotationFloatingStyle(
    rect: AnchorRect,
    options: AnnotationFloatingOptions,
    environment: AnnotationFloatingEnvironment,
): CSSProperties {
    const { viewportWidth, viewportHeight, boundary } = environment;
    const minLeft = Math.max(8, boundary?.left ?? 8);
    const maxRight = Math.min(viewportWidth - 8, boundary?.right ?? viewportWidth - 8);
    const minTop = Math.max(8, boundary?.top ?? 8);
    const maxBottom = Math.min(viewportHeight - 8, boundary?.bottom ?? viewportHeight - 8);
    const maxLeft = Math.max(minLeft, maxRight - options.width);
    const left = Math.min(Math.max(minLeft, rect.left), maxLeft);

    const belowTop = rect.bottom + 8;
    const aboveTop = rect.top - options.height - 8;
    const shouldPlaceAbove = options.placement === "above" ||
        (belowTop + options.height > maxBottom && aboveTop >= minTop);
    const preferredTop = shouldPlaceAbove ? aboveTop : belowTop;
    const availableHeight = Math.max(0, maxBottom - minTop);
    const clampedTop = Math.max(
        minTop,
        maxBottom - Math.min(options.height, availableHeight),
    );
    const top = Math.min(Math.max(minTop, preferredTop), clampedTop);

    return {
        left,
        top,
        maxHeight: Math.max(160, maxBottom - top),
    };
}
